import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Tabs, Form, Input, Button, Spin, message, Modal, Row, Col, Select, DatePicker } from "antd";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";
import notification2 from "@components/Notification2";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { FileManagement } from "@components/FileManagement";
import RelatedWorkorder from "@containers/facility/Equipment/RelatedWorkorder/RelatedWorkorder";
import FunctionalTesting from "@containers/facility/Equipment/FunctionalTesting/FunctionalTesting";
import _ from "lodash";
import "./index.css";
import { useAuthEditable, useAuthFullAccess } from "@/hooks/use-auth";
import { EventName, emitEvent } from "@/hooks/useEventBus";

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const EquipmentDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const editable = useAuthEditable('equipment');
  const fullAccess = useAuthFullAccess('equipment');
  
  // Determine if this is a create page from route
  const isCreatePage = isAdd || location.pathname === '/facility/create-equipment';
  
  const [loading, setLoading] = useState(isCreatePage ? false : true);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [equipmentData, setEquipmentData] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryMap, setCategoryMap] = useState({}); // Map category name to full record
  const [activeTabKey, setActiveTabKey] = useState("1");

  const userName = useSelector(state => 
    state?.Auth?.user || 
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );

  useEffect(() => {
    fetchCategoryList();
    if (!isCreatePage && id) {
      fetchEquipmentDetail();
    } else if (isCreatePage) {
      // For create mode, set initial values
      form.setFieldsValue({
        condition: 'Null'
      });
    }
  }, [id, isCreatePage]);

  useEffect(() => {
    if (equipmentData) {
      setSelectedRecord(equipmentData);
    }
  }, [equipmentData]);

  // Fetch category list
  const fetchCategoryList = async () => {
    try {
      const response = await api.filterEquipmentCategory('page=1&page_size=999');
      if (response.status === 200 && response.data.results) {
        const categories = response.data.results;
        const options = categories.map(cat => ({
          label: cat.name,
          value: cat.name,
        }));
        setCategoryOptions(options);
        
        // Create a map for quick lookup
        const map = {};
        categories.forEach(cat => {
          map[cat.name] = cat;
        });
        setCategoryMap(map);
      }
    } catch (error) {
      console.error("Error fetching category list:", error);
    }
  };

  // Handle category change - update key_spec_name fields
  const handleCategoryChange = (categoryName) => {
    const categoryRecord = categoryMap[categoryName];
    if (categoryRecord) {
      // Update key_spec_name fields based on category spec fields
      const specUpdates = {};
      for (let i = 1; i <= 8; i++) {
        const specField = `spec${i}`;
        const keySpecNameField = `key_spec_${i}_name`;
        if (categoryRecord[specField]) {
          specUpdates[keySpecNameField] = categoryRecord[specField];
        } else {
          specUpdates[keySpecNameField] = '';
        }
      }
      form.setFieldsValue(specUpdates);
    }
  };

  const fetchEquipmentDetail = async () => {
    try {
      setLoading(true);
      const response = await api.filterEquipment(`id=${id}`);
      
      if (response.status === 200 && response.data.results.length > 0) {
        const data = response.data.results[0];
        setEquipmentData(data);
        
        // Set form values with date conversion
        const formValues = {
          ...data,
          install_date: data.install_date ? moment(data.install_date) : null,
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
        };
        form.setFieldsValue(formValues);
      } else {
        message.error("Equipment not found");
      }
    } catch (error) {
      console.error("Error fetching equipment:", error);
      message.error("Failed to load equipment details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setReadOnly(false);
  };

  const handleCancel = () => {
    setReadOnly(true);
    if (equipmentData) {
      const formValues = {
        ...equipmentData,
        install_date: equipmentData.install_date ? moment(equipmentData.install_date) : null,
        created_date: equipmentData.created_date ? moment(equipmentData.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
        modified_date: equipmentData.modified_date ? moment(equipmentData.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
      };
      form.setFieldsValue(formValues);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Format dates before submitting
      const submitValues = {
        ...values,
        install_date: values.install_date ? values.install_date.format("YYYY-MM-DD") : null,
      };
      
      let response;
      if (isCreatePage) {
        // Create mode
        response = await api.createEquipment(submitValues);
      } else {
        // Edit mode
        response = await api.editEquipment(id, submitValues);
      }
      
      if (response.status === 200 || response.status === 201) {
        notification2.success({
          message: "Success",
          description: isCreatePage ? "Equipment created successfully" : "Equipment updated successfully",
        });
        
        // Emit event to refresh equipment list
        emitEvent(EventName.REFRESH_EQUIPMENT_LIST);
        
        if (isCreatePage) {
          // Refresh Equipment list page and close current tab
          refreshTab("/facility/equipment");
          closeTab();
          navigate("/facility/equipment");
        } else {
          setReadOnly(true);
          fetchEquipmentDetail();
        }
      } else {
        notification2.error({
          message: "Error",
          description: isCreatePage ? "Failed to create equipment" : "Failed to update equipment",
        });
      }
    } catch (error) {
      console.error(`Error ${isCreatePage ? 'creating' : 'updating'} equipment:`, error);
      notification2.error({
        message: "Error",
        description: isCreatePage ? "Failed to create equipment" : "Failed to update equipment",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this equipment?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteEquipment(id);
          
          if (response.status === 204 || response.status === 200) {
            notification2.success({
              message: "Success",
              description: "Equipment deleted successfully",
            });
            // Refresh Equipment list page and close current tab
            refreshTab("/facility/equipment");
            closeTab();
            navigate("/facility/equipment");
          } else {
            notification2.error({
              message: "Error",
              description: "Failed to delete equipment",
            });
          }
        } catch (error) {
          console.error("Error deleting equipment:", error);
          notification2.error({
            message: "Error",
            description: "Failed to delete equipment",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancelCreate = () => {
    closeTab();
    navigate("/facility/equipment");
  };

  const renderOperationButton = () => {
    if (isCreatePage) {
      // Create mode - only show Add button
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark pr-2"></i>Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
        </div>
      );
    }
    
    if (readOnly) {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
         {editable &&  <Button type="primary" onClick={handleEdit}>
            <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
          </Button>}
          {fullAccess && <Button danger onClick={handleDelete}>
            <i className="fa-solid fa-trash pr-2"></i>Delete
          </Button>}
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      );
    }
  };

  // Related component
  const EquipmentRelatedComponent = () => {
    return <>{id ? <RelatedWorkorder relatedList={true} parentEquipment={id} relatedObject={'equipment'} /> : ''}</>
  };

  // Functional Testing component
  const FunctionalTestingComponent = () => {
    return <>{selectedRecord ? <FunctionalTesting selectedRecord={selectedRecord} /> : ''}</>
  };

  if (loading && !equipmentData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }
    
    return (
    <div className="equipment-detail-container">
      <Spin spinning={loading}>
        <Tabs 
          defaultActiveKey="1" 
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
        >
          <TabPane
            tab={
              <span>
                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
              </span>
            }
            key="1"
          >
            <div>
              <div className="slds-m-around_x-small">
                <div className="px-4">
                  <div className="w-full">
                    <Form
                      form={form}
                      onFinish={handleSubmit}
                      layout="horizontal"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      className="detailForm"
                      // size="small"
                    >
                      <Row gutter={[0, 0]}>
                        {/* ID - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="ID" name="id">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Name */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Name" 
                            name="name"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Category */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Category" 
                            name="category"
                            rules={[{ required: true, message: "This field is required" }]}
                          >
                            {readOnly ? (
                              <Input disabled value={equipmentData?.category || ""} />
                            ) : (
                              <Select 
                                disabled={readOnly}
                                placeholder="Select Category"
                                onChange={handleCategoryChange}
                                showSearch
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={categoryOptions}
                              />
                            )}
                          </Form.Item>
                        </Col>

                        {/* Service */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Service" name="service">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Condition */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Condition" 
                            name="condition"
                            rules={[{ required: true, message: "This field is required" }]}
                            initialValue="Null"
                          >
                            <Select disabled={readOnly}>
                              <Option value="Null">Null</Option>
                              <Option value="Excellent">Excellent</Option>
                              <Option value="Good">Good</Option>
                              <Option value="Fair">Fair</Option>
                              <Option value="Poor">Poor</Option>
                              <Option value="Failed">Failed</Option>
                            </Select>
                          </Form.Item>
                        </Col>

                        {/* Building */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Building" name="building">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Location */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Location" name="location">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Install Date */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Install Date" name="install_date">
                            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Manufacturer */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Manufacturer" name="Manufacturer">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Equipment Model */}
                        <Col xs={24} lg={12}>
                          <Form.Item label="Equipment Model" name="Equipment_Model">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>

                        {/* Equipment SN */}

                        <Col xs={24} lg={24}></Col>
                        {/* Description - Full Width */}
                        <Col xs={24} lg={12}>
                          <Form.Item 
                            label="Description" 
                            name="description" 
                          >
                            <TextArea rows={3} disabled={readOnly} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <Form.Item label="Equipment SN" name="Equipment_SN">
                            <Input disabled={readOnly} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} lg={24}></Col>
                        {/* Key Specs */}
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                          <React.Fragment key={num}>
                            <Col xs={24} lg={12}>
                              <Form.Item label={`Key Spec ${num} Name`} name={`key_spec_${num}_name`}>
                                <Input disabled />
                              </Form.Item>
                            </Col>
                            <Col xs={24} lg={12}>
                              <Form.Item label={`Key Spec ${num} Value`} name={`key_spec_${num}_value`}>
                                <Input disabled={readOnly} />
                              </Form.Item>
                            </Col>
                          </React.Fragment>
                        ))}

                        {/* Equipment Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Equipment Score" name="equipment_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Equipment Age Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Equipment Age Score" name="equipment_age_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Visual Inspection Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Visual Inspection Score" name="visual_inspection_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Deficiency Score - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Deficiency Score" name="deficiency_score">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Last Condition Update - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Last Condition Update" name="last_condition_update">
                              <Input disabled />
                            </Form.Item>
                          </Col>
                        )}

                        {/* Created By - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Created By" name="createdby_name">
                              <span>{equipmentData?.createdby_name}</span>
                            </Form.Item>
                          </Col>
                        )}

                        {/* Created Date - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Created Date" name="created_date">
                              <span>{equipmentData?.created_date ? moment(equipmentData.created_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
                            </Form.Item>
                          </Col>
                        )}

                        {/* Modified By - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Modified By" name="lastModified_name">
                              <span>{equipmentData?.lastModified_name}</span>
                            </Form.Item>
                          </Col>
                        )}

                        {/* Modified Date - Hidden in create mode */}
                        {!isCreatePage && (
                          <Col xs={24} lg={12}>
                            <Form.Item label="Modified Date" name="modified_date">
                              <span>{equipmentData?.modified_date ? moment(equipmentData.modified_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Related, Attachments, Function Testing Form and Log tabs - Hidden in create mode */}
          {!isCreatePage && (
            <>
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-table-list pr-2"></i>Related
                  </span>
                }
                key="2"
              >
                <div>
                  <EquipmentRelatedComponent />
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-file-pdf pr-2"></i>Attachments
                  </span>
                }
                key="3"
              >
                {equipmentData ? (
                  <FileManagement
                    tableName="equipment"
                    recordId={id}
                    equipmentId={id}
                  />
                ) : (
                  ""
                )}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-flask pr-2"></i>Function Testing Form
                  </span>
                }
                key="4"
              >
        <div>
                  <FunctionalTestingComponent />
        </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-timeline pr-2"></i>Log
                  </span>
                }
                key="5"
              >
                <div className="px-4">
                  <RecordAuditTimeline moduleId={id} module="Equipment" />
                </div>
              </TabPane>
            </>
          )}
        </Tabs>
      </Spin>
    </div>
  );
};

export default EquipmentDetail;
